/**
 * Utilities for creating new pipeline blocks
 */
import { nanoid } from 'nanoid';

import type { PipelineBlock, PipelineConfiguration } from '@mai/types';

import type { BlockDefinition } from '../components/BlockPalette';

export interface CreateBlockOptions {
  position?: { x: number; y: number };
  id?: string;
  config?: Record<string, unknown>;
}

/**
 * Generate a unique block ID
 */
export function generateBlockId(blockType: string): string {
  return `${blockType}_${nanoid(8)}`;
}

/**
 * Create a new pipeline block from a block definition
 */
export function createBlockFromDefinition(
  blockDefinition: BlockDefinition,
  options: CreateBlockOptions = {},
): PipelineBlock {
  const blockId = options.id || generateBlockId(blockDefinition.type);

  // Merge default config with provided config
  const config = {
    ...blockDefinition.defaultConfig,
    ...options.config,
  };

  const block = {
    id: blockId,
    type: blockDefinition.type,
    config,
  };

  // Type assertion to ensure proper typing
  return block as PipelineBlock;
}

/**
 * Add a new block to a pipeline configuration
 */
export function addBlockToPipelineConfig(
  pipelineConfig: PipelineConfiguration,
  block: PipelineBlock,
  _position?: { x: number; y: number },
): PipelineConfiguration {
  return {
    ...pipelineConfig,
    blocks: {
      ...pipelineConfig.blocks,
      [block.id]: block,
    },
  };
}

/**
 * Remove a block from a pipeline configuration
 */
export function removeBlockFromPipelineConfig(
  pipelineConfig: PipelineConfiguration,
  blockId: string,
): PipelineConfiguration {
  const { [blockId]: _removedBlock, ...remainingBlocks } =
    pipelineConfig.blocks;

  // Also remove any connections involving this block
  const filteredConnections = pipelineConfig.connections.filter(
    (connection) =>
      connection.source_block_id !== blockId &&
      connection.target_block_id !== blockId,
  );

  return {
    ...pipelineConfig,
    blocks: remainingBlocks,
    connections: filteredConnections,
  };
}

/**
 * Find optimal position for a new block
 */
export function findOptimalBlockPosition(
  pipelineConfig: PipelineConfiguration,
  canvasPosition?: { x: number; y: number },
): { x: number; y: number } {
  // If canvas position is provided, use it
  if (canvasPosition) {
    return canvasPosition;
  }

  // Default positioning logic
  const blockCount = Object.keys(pipelineConfig.blocks).length;
  const HORIZONTAL_SPACING = 250;
  const VERTICAL_SPACING = 140;

  // Simple grid layout for new blocks
  const x = (blockCount % 4) * HORIZONTAL_SPACING;
  const y = Math.floor(blockCount / 4) * VERTICAL_SPACING;

  return { x, y };
}

/**
 * Validate block configuration
 */
export function validateBlockConfig(
  blockType: string,
  config: Record<string, unknown>,
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Basic validation rules
  switch (blockType) {
    case 'input':
      if (!config.json_schema) {
        errors.push('Input block requires json_schema configuration');
      }
      break;

    case 'output':
      if (!config.name || typeof config.name !== 'string') {
        errors.push('Output block requires a name');
      }
      break;

    case 'constant':
      if (config.config_value === undefined) {
        errors.push('Constant block requires config_value');
      }
      break;

    case 'dataset':
      if (!config.dataset_id || typeof config.dataset_id !== 'string') {
        errors.push('Dataset block requires dataset_id');
      }
      break;

    case 'file':
      if (!config.file_id || typeof config.file_id !== 'string') {
        errors.push('File block requires file_id');
      }
      break;

    case 'llm':
      if (!config.provider) {
        errors.push('LLM block requires provider');
      }
      break;

    case 'clause_extraction_agent':
      if (!config.instructions || typeof config.instructions !== 'string') {
        errors.push('Clause extraction block requires instructions');
      }
      break;

    case 'legal_variance_determination':
      if (!config.instructions || typeof config.instructions !== 'string') {
        errors.push('Legal variance determination block requires instructions');
      }
      break;

    case 'map':
      if (!config.target_block_type) {
        errors.push('Map block requires target_block_type');
      }
      break;

    case 'join':
      if (!config.base_dataset) {
        errors.push('Join block requires base_dataset');
      }
      break;

    case 'string_template':
      if (!config.template || typeof config.template !== 'string') {
        errors.push('String template block requires template');
      }
      break;
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Get default configuration for a block type
 */
export function getDefaultBlockConfig(
  blockType: string,
): Record<string, unknown> {
  switch (blockType) {
    case 'input':
      return {
        json_schema: {
          type: 'object',
          properties: {
            data: { type: 'string' },
          },
        },
      };

    case 'output':
      return { name: 'output' };

    case 'constant':
      return { config_value: '' };

    case 'dataset':
      return { dataset_id: '' };

    case 'file':
      return { file_id: '' };

    case 'identity':
      return {};

    case 'zip':
      return { combine_strategy: 'merge_objects' };

    case 'map':
      return {
        target_block_type: '',
        target_config: {},
        max_concurrency: 5,
      };

    case 'join':
      return {
        base_dataset: '',
        joins: [],
        output_columns: [],
      };

    case 'llm':
      return {
        provider: 'openai',
        model: 'small',
        temperature: 0.2,
        max_tokens: 8192,
      };

    case 'embeddings':
      return {
        model: 'sentence-transformers/static-retrieval-mrl-en-v1',
        batch_size: 100,
        normalize: true,
      };

    case 'semantic_filter':
      return {
        embedding_model: 'sentence-transformers/static-retrieval-mrl-en-v1',
        filter_strategy: 'cosine_similarity_top_k',
        top_k: 10,
        similarity_threshold: 0,
      };

    case 'clause_extraction_agent':
      return {
        instructions: 'Search for relevant clauses in the document',
      };

    case 'legal_variance_determination':
      return {
        instructions: 'Analyze extracted clauses for material variances',
        variance_guidance: '',
        clause_definition: '',
        transaction_context: '',
      };

    case 'string_template':
      return {
        template: '',
        strip_whitespace: true,
      };

    case 'text_splitter':
      return {
        chunk_size: 1000,
        chunk_overlap: 200,
      };

    case 'transform':
      return { operations: [] };

    case 'pdf_reader':
      return {};

    default:
      return {};
  }
}

/**
 * Check if a block can be connected to another block
 */
export function canConnectBlocks(
  sourceBlockType: string,
  targetBlockType: string,
  sourcePort: string,
  targetPort: string,
): { canConnect: boolean; reason?: string } {
  // Basic connection rules - these can be expanded

  // Output blocks can't have outgoing connections
  if (sourceBlockType === 'output') {
    return {
      canConnect: false,
      reason: 'Output blocks cannot have outgoing connections',
    };
  }

  // Input blocks can't have incoming connections
  if (targetBlockType === 'input' || targetBlockType === 'constant') {
    return {
      canConnect: false,
      reason: 'Input and constant blocks cannot have incoming connections',
    };
  }

  // Check port compatibility (simplified)
  const sourceOutputPorts = getBlockOutputPorts(sourceBlockType);
  const targetInputPorts = getBlockInputPorts(targetBlockType);

  if (!sourceOutputPorts.includes(sourcePort)) {
    return { canConnect: false, reason: `Invalid source port: ${sourcePort}` };
  }

  if (!targetInputPorts.includes(targetPort)) {
    return { canConnect: false, reason: `Invalid target port: ${targetPort}` };
  }

  return { canConnect: true };
}

/**
 * Get available output ports for a block type
 */
export function getBlockOutputPorts(blockType: string): string[] {
  switch (blockType) {
    case 'constant':
      return ['value'];
    case 'input':
      return ['data'];
    case 'dataset':
      return ['data'];
    case 'file':
      return ['data'];
    case 'identity':
      return ['output'];
    case 'zip':
      return ['result'];
    case 'map':
      return ['result'];
    case 'join':
      return ['result'];
    case 'llm':
      return ['response'];
    case 'embeddings':
      return ['embeddings'];
    case 'semantic_filter':
      return ['filtered_results'];
    case 'clause_extraction_agent':
      return ['results'];
    case 'legal_variance_determination':
      return ['results'];
    case 'string_template':
      return ['result'];
    case 'json_template':
      return ['result'];
    case 'text_splitter':
      return ['chunks'];
    case 'transform':
      return ['transformed_data'];
    case 'pdf_reader':
      return ['markdown_text'];
    default:
      return ['output'];
  }
}

/**
 * Get available input ports for a block type
 */
export function getBlockInputPorts(blockType: string): string[] {
  switch (blockType) {
    case 'output':
      return ['data'];
    case 'identity':
      return ['input'];
    case 'zip':
      return ['left_array', 'right_array'];
    case 'map':
      return ['array'];
    case 'join':
      return [
        'dataset_0',
        'dataset_1',
        'dataset_2',
        'dataset_3',
        'dataset_4',
        'dataset_5',
        'dataset_6',
        'dataset_7',
        'dataset_8',
        'dataset_9',
      ];
    case 'llm':
      return ['messages'];
    case 'embeddings':
      return ['texts'];
    case 'semantic_filter':
      return ['candidates', 'query'];
    case 'clause_extraction_agent':
      return ['file_id', 'dataset_id', 'context_dataset_id'];
    case 'legal_variance_determination':
      return ['extracted_clauses', 'clause_definition_dataset_id', 'benchmark_dataset_ids', 'research_dataset_ids'];
    case 'string_template':
      return ['data'];
    case 'json_template':
      return ['data'];
    case 'text_splitter':
      return ['text'];
    case 'transform':
      return ['data'];
    case 'pdf_reader':
      return ['pdf_data'];
    case 'file':
      return ['file_id']; // Config port
    case 'dataset':
      return ['dataset_id']; // Config port
    case 'input':
      return []; // Input blocks have no input ports
    case 'constant':
      return []; // Constant blocks have no input ports
    default:
      return ['input'];
  }
}
